import { useEffect, useState } from "react";
import { LocaleLink } from "@/components/site/LocaleLink";
import { Button } from "@/components/ui/button";
import { useConsent } from "@/lib/consent/ConsentProvider";
import { useT } from "@/lib/i18n/I18nProvider";

export function CookieBanner() {
  const {
    consent,
    ready,
    preferencesOpen,
    closePreferences,
    acceptAll,
    rejectAll,
    setCategories,
  } = useConsent();
  const { t } = useT();
  const [showDetails, setShowDetails] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    if (preferencesOpen) {
      setShowDetails(true);
      setAnalytics(!!consent?.categories.analytics);
      setMarketing(!!consent?.categories.marketing);
    }
  }, [preferencesOpen, consent]);

  const visible = ready && (!consent || preferencesOpen);
  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-live="polite"
      aria-label={t("cookie.title")}
      className="motion-safe:animate-in motion-safe:zoom-in-95 motion-safe:duration-300 fixed left-1/2 top-1/2 z-[60] w-[calc(100%-1.5rem)] max-w-[640px] -translate-x-1/2 -translate-y-1/2"
    >
      <div className="rounded-2xl border border-border/70 bg-background/95 p-5 shadow-soft backdrop-blur-md">
        <p className="text-[13.5px] font-semibold text-foreground">{t("cookie.title")}</p>
        <p className="mt-1 text-[12.5px] leading-relaxed text-foreground/75">
          {t("cookie.bodyBefore")}
          <LocaleLink to="/cookie-policy" className="underline underline-offset-2">{t("cookie.bodyLink")}</LocaleLink>
          {t("cookie.bodyAfter")}
        </p>

        {showDetails && (
          <div className="mt-4 space-y-3 rounded-xl border border-border/60 bg-muted/30 p-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-[12.5px] font-medium text-foreground">{t("cookie.cat.necessary")}</p>
                <p className="text-[12px] text-muted-foreground">{t("cookie.cat.necessaryDesc")}</p>
              </div>
              <input
                type="checkbox"
                checked
                disabled
                aria-label={t("cookie.cat.necessary")}
                className="mt-1 h-4 w-4 accent-accent"
              />
            </div>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-[12.5px] font-medium text-foreground">{t("cookie.cat.analytics")}</p>
                <p className="text-[12px] text-muted-foreground">{t("cookie.cat.analyticsDesc")}</p>
              </div>
              <input
                type="checkbox"
                checked={analytics}
                onChange={(e) => setAnalytics(e.target.checked)}
                aria-label={t("cookie.cat.analytics")}
                className="mt-1 h-4 w-4 accent-accent"
              />
            </div>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-[12.5px] font-medium text-foreground">{t("cookie.cat.marketing")}</p>
                <p className="text-[12px] text-muted-foreground">{t("cookie.cat.marketingDesc")}</p>
              </div>
              <input
                type="checkbox"
                checked={marketing}
                onChange={(e) => setMarketing(e.target.checked)}
                aria-label={t("cookie.cat.marketing")}
                className="mt-1 h-4 w-4 accent-accent"
              />
            </div>
          </div>
        )}

        {/* Hover feedback scoped to the banner only - other buttons on the site keep their own styles */}
        <div className="mt-4 flex flex-wrap gap-2 [&_button]:transition-all [&_button]:duration-200 [&_button:hover]:-translate-y-0.5 [&_button:hover]:brightness-110 [&_button:active]:translate-y-0">
          <Button size="sm" variant="gradient" onClick={acceptAll}>{t("cookie.acceptAll")}</Button>
          <Button size="sm" variant="outline" onClick={rejectAll}>{t("cookie.rejectNonEssential")}</Button>
          {showDetails ? (
            <Button size="sm" variant="ghost" onClick={() => setCategories({ analytics, marketing })}>
              {t("cookie.savePreferences")}
            </Button>
          ) : (
            <Button size="sm" variant="ghost" onClick={() => setShowDetails(true)}>
              {t("cookie.manage")}
            </Button>
          )}
          {preferencesOpen && (
            <Button size="sm" variant="ghost" onClick={closePreferences}>
              {t("cookie.close")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
